import { useCallback, useEffect, useRef, useState } from "react";

const SCROLL_THRESHOLD = 24;

const isAtBottom = (el) =>
  el.scrollHeight - el.scrollTop - el.clientHeight <= SCROLL_THRESHOLD;

const useScrolledToBottom = (contentReady = true) => {
  const scrollRef = useRef(null);
  const [scrolledToBottom, setScrolledToBottom] = useState(false);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return false;

    const hasScrollableContent =
      el.scrollHeight > el.clientHeight + SCROLL_THRESHOLD;

    if (!hasScrollableContent) {
      if (el.scrollHeight > 50) {
        setScrolledToBottom(true);
      }
      return !hasScrollableContent;
    }

    const atBottom = isAtBottom(el);
    if (atBottom) {
      setScrolledToBottom(true);
    }
    return atBottom;
  }, []);

  useEffect(() => {
    setScrolledToBottom(false);
  }, [contentReady]);

  useEffect(() => {
    if (!contentReady) return undefined;

    const el = scrollRef.current;
    if (!el) return undefined;

    const onScroll = () => {
      checkScroll();
    };

    el.addEventListener("scroll", onScroll, { passive: true });

    let resizeObserver;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => {
        checkScroll();
      });
      resizeObserver.observe(el);
      Array.from(el.children).forEach((child) => resizeObserver.observe(child));
    }

    const rafId = requestAnimationFrame(() => {
      checkScroll();
    });

    return () => {
      el.removeEventListener("scroll", onScroll);
      resizeObserver?.disconnect();
      cancelAnimationFrame(rafId);
    };
  }, [checkScroll, contentReady]);

  return { scrollRef, scrolledToBottom, checkScroll };
};

export default useScrolledToBottom;
